/**
 * Human-readable segment for Community Signal URLs:
 * `/community/<slug>/token/<mint>`
 *
 * Never use the slug `token` — it collides with the `/token/` path segment and
 * produces `/community/token/token/<mint>`, which Google flags as Soft 404.
 */
const RESERVED_COMMUNITY_SLUGS = new Set(["token", "community-seo"]);

export function slugifyCommunityTokenName(raw) {
  const s = String(raw ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  const slug = s
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 96);
  if (!slug || RESERVED_COMMUNITY_SLUGS.has(slug)) return "";
  return slug;
}

/** Path slug: readable name, or a mint prefix when the name is empty/reserved. */
export function communityTokenUrlSlug(tokenNameOrSymbol, mint) {
  const slug = slugifyCommunityTokenName(tokenNameOrSymbol);
  if (slug) return slug;
  const compact = String(mint ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .slice(0, 8);
  return compact && !RESERVED_COMMUNITY_SLUGS.has(compact) ? compact : "signal";
}

const SIGNAL_DATE_YMD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Validate `signalDate` query from searchParams (Community Signal Star pick date).
 * Returns ISO day + DD/MM/YYYY for display, or null if invalid/out of range.
 */
export function parseSignalDateSearchParam(value) {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw == null || typeof raw !== "string") return null;
  const s = raw.trim();
  if (!SIGNAL_DATE_YMD.test(s)) return null;
  const y = Number(s.slice(0, 4));
  const mo = Number(s.slice(5, 7));
  const d = Number(s.slice(8, 10));
  if (y < 2020 || y > 2040 || mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (
    dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() !== mo - 1 ||
    dt.getUTCDate() !== d
  ) {
    return null;
  }
  const isoYmd = `${String(y).padStart(4, "0")}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const ddMmYyyy = `${String(d).padStart(2, "0")}/${String(mo).padStart(2, "0")}/${y}`;
  return { isoYmd, ddMmYyyy };
}

/**
 * Client and server: path + optional `signalDate` query (YYYY-MM-DD calendar day).
 * @param {{ signalDateForQuery?: string | null }} [options] — If set, must be `YYYY-MM-DD` (typically from bot creation UTC calendar).
 */
export function communitySignalPagePath(mint, tokenNameOrSymbol, options) {
  const m = String(mint ?? "").trim();
  const slug = communityTokenUrlSlug(tokenNameOrSymbol, m);
  const path = `/community/${slug}/token/${encodeURIComponent(m)}`;
  const sd = options?.signalDateForQuery?.trim();
  if (!sd || !SIGNAL_DATE_YMD.test(sd)) return path;
  const y = Number(sd.slice(0, 4));
  if (y < 2020 || y > 2040) return path;
  const qs = new URLSearchParams({ signalDate: sd });
  return `${path}?${qs.toString()}`;
}

export function communitySignalPagePathFromPayload(payload) {
  if (!payload?.mint) return "/";
  const label =
    payload.jupiter?.name || payload.jupiter?.symbol || "";
  return communitySignalPagePath(payload.mint, label);
}

export function communitySignalRequestPath(tokenSlug, mint) {
  const m = String(mint ?? "").trim();
  const slug = String(tokenSlug ?? "").trim();
  if (!m || !slug) return "";
  return `/community/${slug}/token/${encodeURIComponent(m)}`;
}

export function canonicalCommunitySignalUrl(origin, mint, tokenLabel) {
  const base = String(origin ?? "").replace(/\/$/, "");
  return `${base}${communitySignalPagePath(mint, tokenLabel)}`;
}
